import {
  Bar,
  BarChart as RechartsBarChart,
  CartesianGrid,
  Cell,
  ReferenceLine,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { ChartFrame } from '../../molecules/ChartFrame/ChartFrame';
import { CategoryTick, RowCategoryTick } from '../../molecules/ChartFrame/CategoryTick';
import type { CartesianChartProps } from '../../molecules/ChartFrame/chartProps';
import { chartAriaLabel, isEmpty, seriesPayload } from '../../molecules/ChartFrame/chartProps';
import { AnchoredTooltipSurface } from '../../molecules/ChartFrame/AnchoredTooltipSurface';
import { useAnchoredTooltip } from '../../molecules/ChartFrame/useAnchoredTooltip';
import {
  axisBaselineProps,
  categoryAxisProps,
  chartAnimation,
  chartBarGeometry,
  chartMarkGeometry,
  divergingScale,
  gridProps,
  legendItems,
  referenceLineProps,
  seriesColor,
  tooltipCursor,
  valueAxisProps,
  xAxisProps,
  yAxisProps,
  type ChartSeriesToken,
} from '../../molecules/ChartFrame/chartTheme';
import { ChartLegend } from '../../molecules/ChartLegend/ChartLegend';
import { ChartTooltip } from '../../molecules/ChartTooltip/ChartTooltip';

/**
 * A bar chart, drawn with the Figma chart chrome.
 *
 * Shares `LineChart`'s API for everything the two have in common, which is the
 * point of putting the chrome in one place. Figma specifies nothing about the
 * bars themselves — no width, no corner radius, no rotated variant — so the
 * marks come from `chartBarGeometry`, where each value names what it was
 * derived from. See plan.md §7 item 132.
 *
 * ```tsx
 * <BarChart data={rows} xKey="month" series={series} stacked />
 * <BarChart data={rows} xKey="topic" series={sentiment} stacked normalize orientation="horizontal" />
 * ```
 */
export type BarChartProps = CartesianChartProps & {
  /**
   * Stack the series into one bar per category instead of drawing them side by
   * side.
   */
  stacked?: boolean;
  /**
   * Which way the bars run. `vertical` draws columns standing on the category
   * axis; `horizontal` rotates the plot so categories run down the left and the
   * bars extend rightwards — the shape to reach for when the category names are
   * words rather than dates.
   *
   * Note this is the opposite sense to Recharts' own `layout` prop, which names
   * the axis rather than the bars. The translation happens here so a caller
   * never has to hold that inversion in their head.
   */
  orientation?: 'vertical' | 'horizontal';
  /**
   * Scale every category to a full bar, so the chart reads as composition
   * rather than volume — "what is this topic made of", not "how big is it".
   *
   * Stacked only: normalising side-by-side bars would make every bar full
   * height and say nothing. Ignored unless `stacked`.
   */
  normalize?: boolean;
  /** Draw a reference line at this value on the value axis. */
  threshold?: number;
  /**
   * Width of the gutter reserved for category labels when `horizontal`.
   * Defaults to `chartBarGeometry.categoryAxisWidth`.
   */
  categoryAxisWidth?: number;
  /**
   * Colour each bar by its own position rather than by series. Single-series
   * only — with more than one series the colour already carries the series and
   * this would overwrite it.
   *
   * A `ChartSeriesToken[]`, never colour strings, so a literal hex fails the
   * typecheck. The same prop `DonutChart` takes, for the same reason.
   */
  cellColors?: ChartSeriesToken[];
  /**
   * Draw these bars at `chartMarkGeometry.mutedOpacity`, by row index.
   * Single-series only, like `cellColors`.
   *
   * For "these bars are in the answer, those are context": a search's matches
   * at full strength, the related words it pulled in faded. Opacity rather than
   * a second hue, because a second hue would read as a second category, and the
   * related bars measure the same thing as the matches.
   */
  mutedCells?: readonly boolean[];
  /**
   * Draw the first of two series to the **left** of a shared zero and the second
   * to the right — a population pyramid.
   *
   * A prop on this component rather than a `PyramidChart` beside it, which is
   * the rule orientation, stacking and normalisation already follow: a diverging
   * plot is a rotated stacked bar with a signed offset and a symmetric scale,
   * not a different chart. Implies `orientation="horizontal"` and its own
   * stacking, so neither has to be passed with it.
   *
   * **The caller passes plain positive values.** The left series is negated
   * here, which keeps the negation a fact about how the chart is drawn rather
   * than one about the data: the rows stay readable, `formatValue` never sees a
   * minus sign it has to strip, and a KPI computed off the same rows cannot
   * accidentally report a segment as `-23`.
   *
   * Exactly two series. A third has no side to go on.
   */
  diverging?: boolean;
};

/** Percent formatter for a normalised plot, where the axis runs 0–1. */
const percentOfWhole = (value: number) => `${Math.round(value * 100)}%`;

export function BarChart({
  data,
  xKey,
  series,
  height,
  legend = 'bottom',
  tooltip = true,
  loading = false,
  empty,
  emptyLabel,
  formatValue,
  formatDetail,
  formatAxisValue,
  xAxisTitle,
  yAxisTitle,
  label,
  stacked = false,
  orientation = 'vertical',
  normalize = false,
  threshold,
  categoryAxisWidth,
  cellColors,
  mutedCells,
  diverging = false,
  yDomain,
  yTicks,
  onViewDetails,
  viewDetailsLabel,
  className,
}: BarChartProps) {
  const items = legendItems(series);
  const rotated = orientation === 'horizontal' || diverging;
  const expand = stacked && normalize && !diverging;

  /** The series drawn to the left of zero. `undefined` unless diverging. */
  const leftKey = diverging ? series[0]?.key : undefined;

  /*
   * The left side is negated on the way into Recharts and nowhere else. `null`
   * and `undefined` are passed through rather than coerced: a missing reading is
   * a gap, and `-0` would draw a bar of nothing at the spine claiming it was
   * measured.
   */
  const plotted =
    leftKey === undefined
      ? data
      : data.map((row) => {
          const value = row[leftKey];
          if (value === null || value === undefined || value === '') return row;
          return { ...row, [leftKey]: -Math.abs(Number(value)) };
        });

  /*
   * Read off the negated rows, so the scale covers what is actually drawn. Both
   * series feed it: a pyramid scaled to the taller side alone would let the
   * other side exceed its own axis.
   */
  const scale = diverging
    ? divergingScale(plotted.flatMap((row) => series.map((s) => Number(row[s.key] ?? 0))))
    : /*
       * `yDomain` and `yTicks` name the **value** axis whichever way the bars
       * run — so on a rotated plot they land on the `XAxis`. Honouring them here
       * closes a gap rather than adding a feature: both have been on
       * `CartesianChartProps` since `LineChart` needed them, and a caller passing
       * either one to a bar chart was getting silence.
       */
      yDomain
      ? { domain: yDomain, ticks: yTicks ?? [] }
      : undefined;

  /* An empty `ticks` array means "you chose the domain, Recharts picks the ticks". */
  const axisScale = scale
    ? { domain: scale.domain, ...(scale.ticks.length > 0 ? { ticks: scale.ticks } : {}) }
    : undefined;

  /* Every label on a diverging axis is a distance from zero, never a signed value. */
  const unsigned = (format?: (value: number) => string) => (value: number) =>
    format ? format(Math.abs(value)) : String(Math.abs(value));

  /*
   * A normalised plot's axis runs 0–1, so the caller's `formatValue` — written
   * for the raw figures — would print "0" against every tick. The percentage is
   * what the axis actually shows, so it wins.
   *
   * `formatAxisValue` falls back to `formatValue`, which is the contract
   * `CartesianChartProps` already declares and `LineChart` already honours —
   * precision that belongs on a reading does not belong on a gridline. Wired up
   * here for the same reason as `yDomain`: the prop was accepted and dropped.
   */
  const tickFormat = formatAxisValue ?? formatValue;
  const axisFormat = expand ? percentOfWhole : diverging ? unsigned(tickFormat) : tickFormat;

  /*
   * The rounded end is the one that floats: the top of a column, the right of a
   * bar. On a stack only the last segment has a floating end — rounding the
   * others would notch the joins between segments.
   *
   * On a diverging plot both series have a floating end and they point opposite
   * ways: the left series floats at its left, the right series at its right.
   * The square ends then meet at the spine.
   */
  const radiusFor = (index: number): number | [number, number, number, number] => {
    const r = chartBarGeometry.radius;
    if (diverging) return index === 0 ? [r, 0, 0, r] : [0, r, r, 0];
    if (stacked && index < series.length - 1) return 0;
    return rotated ? [0, r, r, 0] : [r, r, 0, 0];
  };

  const anchor = useAnchoredTooltip({
    count: plotted.length,
    enabled: tooltip,
    onDrill: onViewDetails,
  });
  /*
   * The *plotted* row, not the caller's. A diverging plot stores its left series
   * negative, and every formatter below — `unsigned`, `share` — is written
   * against that rendering. Reading the readout off `data` instead would print
   * the pyramid's left side as a positive the axis never shows.
   */
  const anchored = anchor.point ? plotted[anchor.point.index] : undefined;

  const kind = diverging
    ? 'Diverging bar chart'
    : `${normalize && stacked ? 'Normalised ' : stacked ? 'Stacked ' : ''}bar chart`;

  return (
    <ChartFrame
      height={height}
      xAxisTitle={xAxisTitle}
      yAxisTitle={yAxisTitle}
      loading={loading}
      empty={empty ?? isEmpty(data, series)}
      emptyLabel={emptyLabel}
      label={label ?? chartAriaLabel(kind, series, xKey)}
      legend={legend === 'none' ? undefined : <ChartLegend items={items} />}
      legendPlacement="bottom"
      plotRef={anchor.plotRef}
      plotProps={anchor.plotProps}
      hover={
        anchored && (
          <AnchoredTooltipSurface anchor={anchor}>
            <ChartTooltip
              active
              label={anchored[xKey] ?? undefined}
              payload={seriesPayload(anchored, series)}
              /*
               * A normalised stack *draws* proportions from the raw figures the
               * expand offset was computed from — running those through the
               * axis' own percent formatter printed a count times a hundred, and
               * a 13/15/20 stack read "1300%, 1500%, 2000%" (2026-09-23). The
               * division happens in `ChartTooltip`, the only place the point's
               * total is known, and `formatValue` then lands on the count beside
               * the share.
               */
              share={expand}
              formatter={
                /*
                 * The left series' stored value is negative; the reading is not.
                 * A pyramid tooltip announcing "-23 mentions" would report the
                 * rendering rather than the data.
                 */
                diverging
                  ? (v) => unsigned(formatValue)(Number(v))
                  : formatValue && ((v) => formatValue(Number(v)))
              }
              detail={
                formatDetail &&
                ((entry) =>
                  entry.payload ? formatDetail(entry.payload, String(entry.dataKey)) : undefined)
              }
              action={anchor.drill}
              actionLabel={viewDetailsLabel}
              locked={anchor.locked}
            />
          </AnchoredTooltipSurface>
        )
      }
      className={className}
    >
      <RechartsBarChart
        {...anchor.chartProps}
        data={plotted}
        /* Recharts names the axis, not the bars — see `orientation` above. */
        layout={rotated ? 'vertical' : 'horizontal'}
        /*
         * `sign` is what puts the negated series on the other side of zero
         * rather than stacking it end to end with its partner.
         */
        stackOffset={expand ? 'expand' : diverging ? 'sign' : 'none'}
        barGap={chartBarGeometry.barGap}
        barCategoryGap={chartBarGeometry.categoryGap}
      >
        {/*
         * Gridlines mark the *value* axis, so they run across the bars:
         * horizontal rules for columns, vertical ones for a rotated plot. Figma
         * draws only the horizontal case; `gridProps` has carried the rotated
         * one since the chrome was written.
         */}
        <CartesianGrid {...gridProps(rotated ? 'vertical' : 'horizontal')} />

        {rotated ? (
          <>
            <XAxis {...valueAxisProps()} tickFormatter={axisFormat} {...(axisScale ?? {})} />
            <YAxis {...categoryAxisProps(xKey, categoryAxisWidth)} tick={<RowCategoryTick />} />
          </>
        ) : (
          <>
            {/*
             * The category axis of a vertical plot shares the plot's width
             * between its bands, so a long name can run into the one beside
             * it. `CategoryTick` wraps it rather than thinning or truncating —
             * neither of which a category axis survives. It is a no-op for a
             * label that already fits, which is every one before Overview.
             */}
            <XAxis {...xAxisProps(xKey, data.length)} tick={<CategoryTick />} />
            <YAxis {...yAxisProps()} tickFormatter={axisFormat} {...(axisScale ?? {})} />
          </>
        )}

        {tooltip && (
          /* Cursor only — see `LineChart`, where this arrangement is explained. */
          <Tooltip
            {...anchor.cursorProps}
            isAnimationActive={false}
            content={() => null}
            cursor={tooltipCursor('bar')}
          />
        )}

        {threshold !== undefined &&
          (rotated ? (
            <ReferenceLine x={threshold} {...referenceLineProps()} />
          ) : (
            <ReferenceLine y={threshold} {...referenceLineProps()} />
          ))}

        {/* The pyramid's spine. See `axisBaselineProps` for why it is not dashed. */}
        {diverging && <ReferenceLine x={0} {...axisBaselineProps()} />}

        {series.map((s, i) => (
          <Bar
            key={s.key}
            dataKey={s.key}
            name={s.label}
            fill={seriesColor(i, s.color)}
            stackId={stacked || diverging ? 'stack' : undefined}
            radius={radiusFor(i)}
            maxBarSize={chartBarGeometry.maxBarSize}
            {...chartAnimation}
          >
            {/*
             * Per-bar colour is only meaningful on a single series; with more
             * than one the fill already carries the series identity.
             */}
            {(cellColors || mutedCells) && series.length === 1
              ? data.map((_, j) => (
                  <Cell
                    key={j}
                    fill={cellColors ? seriesColor(j, cellColors[j]) : seriesColor(i, s.color)}
                    fillOpacity={mutedCells?.[j] ? chartMarkGeometry.mutedOpacity : 1}
                  />
                ))
              : null}
          </Bar>
        ))}
      </RechartsBarChart>
    </ChartFrame>
  );
}
